import { usePaginatedQuery } from "convex/react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

interface MatterNotesProps {
	connectionId: Id<"oauthConnections">;
	clioMatterId: number;
}

type NoteFilter = "all" | "with_time" | "without_time";

export function MatterNotes({ connectionId, clioMatterId }: MatterNotesProps) {
	const [filter, setFilter] = useState<NoteFilter>("all");
	const pagination = usePaginatedQuery(
		api.clio.queries.listNotes,
		{ connectionId, clioMatterId, filter },
		{ initialNumItems: 20 },
	);

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="flex items-center gap-2 px-4 py-2">
				<Tabs
					value={filter}
					onValueChange={(v) => {
						if (v === "all" || v === "with_time" || v === "without_time") {
							setFilter(v);
						}
					}}
				>
					<TabsList>
						<TabsTrigger value="all">All</TabsTrigger>
						<TabsTrigger value="with_time">With time</TabsTrigger>
						<TabsTrigger value="without_time">Without time</TabsTrigger>
					</TabsList>
				</Tabs>
			</div>
			<Separator />
			{pagination.status === "LoadingFirstPage" &&
			pagination.results.length === 0 ? (
				<Empty className="p-6">
					<EmptyContent>
						<EmptyDescription>Loading notes…</EmptyDescription>
					</EmptyContent>
				</Empty>
			) : pagination.results.length === 0 ? (
				<Empty className="p-6">
					<EmptyContent>
						<EmptyDescription>No notes on this matter yet.</EmptyDescription>
					</EmptyContent>
				</Empty>
			) : (
				<ScrollArea className="h-full min-h-0 flex-1">
					<div className="flex flex-col divide-y">
						{pagination.results.map((note) => (
							<div key={note._id} className="flex flex-col gap-2 p-4">
								<div className="flex items-baseline gap-2">
									<div className="font-medium text-sm">
										{note.subject ?? "Untitled note"}
									</div>
									<div className="ml-auto text-xs text-muted-foreground">
										{note.date ??
											new Date(note.clioCreatedAt).toLocaleDateString()}
									</div>
								</div>
								{note.detail && (
									<div className="whitespace-pre-wrap text-sm text-foreground/90">
										{note.detail}
									</div>
								)}
								<div className="text-xs text-muted-foreground">
									{note.authorName ?? "—"}
									{note.hasTimeEntry && " · time logged"}
								</div>
							</div>
						))}
						{pagination.status === "CanLoadMore" && (
							<div className="p-3">
								<Button
									variant="outline"
									size="sm"
									className="w-full"
									onClick={() => pagination.loadMore(20)}
								>
									Load more
								</Button>
							</div>
						)}
					</div>
				</ScrollArea>
			)}
		</div>
	);
}
